

import React, { memo, useMemo, useState, useCallback, useEffect } from 'react';
import SVG from 'react-inlinesvg';
import cx from 'classnames';

import { empty } from '../../../ui-kit/assets/images';
import { notify } from '../../Notifications';
import { DataTable, EmptyDataPlaceholder, Text, FormModal } from '../../../ui-kit';
import { getTemplatesDataTableColumns, normalizeTemplates } from '../utils';

import TemplateAction from './common/TemplateAction';
import ElevateDetails from './common/ElevateDetails';
import LinkSplashPage from './common/LinkSplashPage';

import '../styles/_elevate-template-list.scss';

const STATUS = Object.freeze({
  PUBLISHED: 'PUBLISHED',
  UNPUBLISHED: 'UNPUBLISHED',
  ARCHIVE: 'ARCHIVE',
  MOVE: 'MOVE',
  CLONE: 'CLONE',
  UPDATE: 'UPDATE',
  DRAFT: 'DRAFT'
});



















const NoDataPlaceholder = ({ orgName }) =>
<div className="list__no-data-container">
    <div className="list__no-data__svg-container">
      <SVG cacheRequests src={empty} uniquifyIDs title="There's nothing here yet." />
    </div>
    <Text heading3 centered noMargin="top">{'There\'s nothing here yet, but we\'re glad you\'re here!'}</Text>
    {orgName &&
  <Text noMargin centered placeholder>
        Create, manage and publish templates to be used by
        {' '}
        {orgName}
      </Text>}

  </div>;


const ManageTemplatesList = (props) => {
  const {
    templates,
    search,
    orgName,
    updateTemplate,
    setTemplateDetails,
    navigateToEditTemplate,
    imageUrl,
    getOrganisationsByName,
    organisationId,
    generatePreview,
    isLoading,
    isInternalAdmin,
    getSplashTemplatesByOrg,
    updatePitchSplashLink,
    isMobile
  } = props;
  const [isPublishVisible, setPublishVisible] = useState(false);
  const [isUnpublishVisible, setUnpublishVisible] = useState(false);
  const [templateId, setTemplateId] = useState(null);
  const [isListLoading, setListLoading] = useState(isLoading);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editDetails, setEditDetails] = useState(null);
  const [showSplashLinkModal, setShowSplashLinkModal] = useState(false);
  const [existingSplashPage, setExistingSplashPage] = useState(null);


  useEffect(() => {
    setListLoading(isLoading);
  }, [isLoading]);

  const onArchive = useCallback(async (selectedTemplateId, status) => {
    const input = {
      templateId: selectedTemplateId,
      status: status === STATUS.ARCHIVE ? STATUS.UNPUBLISHED : STATUS.ARCHIVE
    };
    setListLoading(true);
    await updateTemplate({ templateId: input.templateId, action: input.status });
    await setTemplateDetails();
    setListLoading(false);
  }, [setListLoading, updateTemplate, setTemplateDetails]);

  const onGeneratePreview = async (selectedTemplateId) => {
    if (generatePreview) {
      const { error } = await generatePreview(selectedTemplateId);

      if (!error) {
        notify({
          type: 'success',
          content: 'It may take a few moments to compile your template. When it’s ready, your test video + Splash link will be sent to your email address.',
          title: 'Generating Preview'
        });
      }
    }
  };

  const goToEditPage = (selectedTemplateId) => {
    if (navigateToEditTemplate) {
      navigateToEditTemplate(selectedTemplateId);
    }
  };

  const onMove = (selectedTemplateId) => {
    setTemplateId(selectedTemplateId);
    setShowMoveModal(true);
  };

  const onClone = (selectedTemplateId) => {
    setTemplateId(selectedTemplateId);
    setShowCloneModal(true);
  };


  const onPublish = (selectedTemplateId) => {
    setPublishVisible(true);
    setTemplateId(selectedTemplateId);
  };

  const onPublishModalClose = () => {
    setPublishVisible(false);
    setTemplateId(null);
  };

  const onPublishSave = async () => {
    const input = { templateId, status: STATUS.PUBLISHED };
    setListLoading(true);
    await updateTemplate({ templateId: input.templateId, action: input.status });
    await setTemplateDetails();
    setListLoading(false);
  };

  const onUnPublish = (selectedTemplateId) => {
    setUnpublishVisible(true);
    setTemplateId(selectedTemplateId);
  };

  const onUnpublishModalClose = () => {
    setUnpublishVisible(false);
    setTemplateId(null);
  };

  const onUnpublishSave = async () => {
    const input = { templateId, status: STATUS.UNPUBLISHED };
    setListLoading(true);
    await updateTemplate({ templateId: input.templateId, action: input.status });
    await setTemplateDetails();
    setListLoading(false);
  };

  const onMoveModalClose = () => setShowMoveModal(false);
  const onCloneModalClose = () => setShowCloneModal(false);


  const onDetailsModal = (selectedTemplateId) => {
    const templateDetails = templates && templates.find(
      (template) => template.templateId === selectedTemplateId
    );
    setEditDetails(templateDetails);
    setShowDetailsModal(true);
    setTemplateId(selectedTemplateId);
  };

  const onDetailsModalClose = () => {
    setShowDetailsModal(false);
    setEditDetails(null);
  };

  const onSplashLinkModal = (selectedTemplateId) => {
    const templateDetails = templates && templates.find(
      (template) => template.templateId === selectedTemplateId
    );

    if (templateDetails) {
      setExistingSplashPage(templateDetails.splashTemplatesDescriptorId);
      setShowSplashLinkModal(true);
      setTemplateId(selectedTemplateId);
    }
  };

  const onSplashLinkModalClose = () => {
    setExistingSplashPage(null);
    setShowSplashLinkModal(false);
    setTemplateId(null);
  };

  const options = (selectedTemplateId, status, templateVersion) => [
  ...(status === STATUS.PUBLISHED ? [{
    action: () => onUnPublish(selectedTemplateId),
    label:
    <Text success noMargin suffixIcon="hide" suffixSize={20} suffixIconColor="monkey">
          Unpublish
        </Text>

  }] : []),
  ...(status === STATUS.UNPUBLISHED ? [{
    action: () => onPublish(selectedTemplateId),
    label:
    <Text success noMargin suffixIcon="show" suffixSize={20} suffixIconColor="monkey">
          Publish
        </Text>

  }] : []),
  ...(status !== STATUS.ARCHIVE ? [{
    separator: true
  }] : []),
  ...(isInternalAdmin ? [
  ...(status === STATUS.UNPUBLISHED ? [{
    action: () => goToEditPage(selectedTemplateId),
    label: 'Edit Template'
  }] : []),
  {
    action: () => onMove(selectedTemplateId),
    label: 'Move Template'
  },
  {
    action: () => onClone(selectedTemplateId),
    label: 'Duplicate Template'
  },
  {
    separator: true
  },
  ...(status === STATUS.PUBLISHED ? [{
    label: 'Generate Preview',
    action: () => onGeneratePreview(selectedTemplateId)
  }] : [])] :
  [
  ...(status === STATUS.DRAFT && templateVersion === 2 ? [{
    action: () => goToEditPage(selectedTemplateId),
    label: 'Edit Template'
  }] : []),
  {
    action: () => onDetailsModal(selectedTemplateId),
    label: 'Template Details'
  },
  {
    action: () => onSplashLinkModal(selectedTemplateId),
    label: 'Link Splash Page'
  }]),

  {
    separator: true
  },
  {
    action: () => onArchive(selectedTemplateId, status),
    label: status === STATUS.ARCHIVE ? 'Unarchive' : 'Archive',
    isRed: true
  }];


  const columns = useMemo(() => getTemplatesDataTableColumns({
    isInternalAdmin,
    options,
    onPublish,
    onUnPublish,
    isMobile
  }), [templates, options]);

  if (!templates) {
    return null;
  }

  if (!templates.length && search) {
    return (
      <EmptyDataPlaceholder
        searchTerm={search} />);


  }

  if (!templates.length) {
    return <NoDataPlaceholder orgName={orgName} />;
  }

  return (
    <>
      <div className={cx('template-data-table', { 'template-data-table--no-header': !isInternalAdmin })}>
        <DataTable
          rows={normalizeTemplates(templates)}
          columns={columns}
          isLoading={isListLoading}
          columnFieldId="templateId" />

      </div>
      {isPublishVisible &&
      <FormModal
        visible={isPublishVisible}
        title="Are you sure?"
        onSave={onPublishSave}
        saveText="Publish Template"
        closeText="Cancel"
        onClose={onPublishModalClose}>

          <Text>
            Publishing this template will make it available for use within the selected
            Client Workspace.
          </Text>
        </FormModal>}

      {isUnpublishVisible &&
      <FormModal
        visible={isUnpublishVisible}
        title="Are you sure?"
        onSave={onUnpublishSave}
        saveText="Unpublish Template"
        closeText="Cancel"
        onClose={onUnpublishModalClose}>

          <Text>
            Reverting this template back to Draft will make it not available for use within
            the selected Client Workspace. All previously generated splash links and Elevate videos
            using this template will still be accessible.
          </Text>
        </FormModal>}

      {showMoveModal &&
      <TemplateAction
        visible={showMoveModal}
        title="Move Template"
        templateId={templateId}
        imageUrl={imageUrl}
        orgName={orgName}
        getOrganisationsByName={getOrganisationsByName}
        onClose={onMoveModalClose}
        action={STATUS.MOVE}
        updateTemplate={updateTemplate}
        sourceOrganisationId={organisationId} />}


      {showCloneModal &&
      <TemplateAction
        visible={showCloneModal}
        title="Duplicate Template"
        templateId={templateId}
        imageUrl={imageUrl}
        orgName={orgName}
        getOrganisationsByName={getOrganisationsByName}
        onClose={onCloneModalClose}
        action={STATUS.CLONE}
        updateTemplate={updateTemplate}
        sourceOrganisationId={organisationId} />}



      {showDetailsModal &&
      <ElevateDetails
        visible={showDetailsModal}
        title="Template Details"
        templateId={templateId}
        onClose={onDetailsModalClose}
        action={STATUS.UPDATE}
        updateTemplate={updateTemplate}
        editDetails={editDetails}
        setTemplateDetails={setTemplateDetails} />}



      {showSplashLinkModal &&
      <LinkSplashPage
        visible={showSplashLinkModal}
        title="Link Splash Page"
        templateId={templateId}
        onClose={onSplashLinkModalClose}
        existingSplashPage={existingSplashPage}
        setTemplateDetails={setTemplateDetails}
        getSplashTemplatesByOrg={getSplashTemplatesByOrg}
        updatePitchSplashLink={updatePitchSplashLink} />}


    </>);

};

export default memo(ManageTemplatesList);